import React, { useEffect, useState } from 'react';
import {
    Mail, ArrowRight, Heart, Building2, Users, Globe, Sparkles, Wrench,
    Plus, Minus, Check, X,
} from 'lucide-react';

/**
 * RevoloAIWEB for Good — static informational page.
 * No backend, no forms, no analytics. All CTAs are mailto: links.
 *
 * Accent colour: warm gold #B5871A (only for this page — no global theme change).
 */

const GOLD = '#B5871A';
const GOLD_SOFT = 'rgba(181, 135, 26, 0.12)';
const GOLD_BORDER = 'rgba(181, 135, 26, 0.30)';

const MAILTO =
    'mailto:revoloai@protonmail.com?subject=RevoloAIWEB%20for%20Good%20%E2%80%94%20enquiry';

const ForGood = () => {
    // Per-page metadata. No new dep (would normally use react-helmet); cleans
    // up on unmount so other pages keep their existing <title>.
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'RevoloAIWEB for Good — Digital tools for UK non-profits';

        const setMeta = (attrName, attrValue, content) => {
            // attrName is 'name' or 'property'; attrValue is e.g. 'description' or 'og:title'.
            let tag = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
            const created = !tag;
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute(attrName, attrValue);
                document.head.appendChild(tag);
            }
            const before = tag.getAttribute('content');
            tag.setAttribute('content', content);
            return () => {
                if (created) tag.remove();
                else if (before !== null) tag.setAttribute('content', before);
            };
        };

        const description =
            'Discounted websites, AI tooling and digital training for UK charities, ' +
            'community groups and social enterprises. A programme by Artemis Business ' +
            'Solutions Ltd.';
        const ogTitle = 'RevoloAIWEB for Good — Digital tools for UK non-profits';
        const cleanupDesc = setMeta('name', 'description', description);
        const cleanupOgT  = setMeta('property', 'og:title', ogTitle);
        const cleanupOgD  = setMeta('property', 'og:description', description);

        return () => {
            document.title = prevTitle;
            cleanupDesc(); cleanupOgT(); cleanupOgD();
        };
    }, []);

    return (
        <main className="bg-white text-[#1A1A2E]" data-testid="for-good-page">
            <HeroSection />
            <TrustStrip />
            <WhyExists />
            <WhoItsFor />
            <WhatYouCanGet />
            <HowItWorks />
            <WhatWeDontDo />
            <FAQSection />
            <ClosingCTA />
        </main>
    );
};

/* ============================================================
   1. Hero
   ============================================================ */
const HeroSection = () => (
    <section
        className="relative pt-32 pb-24 sm:pt-40 sm:pb-28 overflow-hidden"
        data-testid="for-good-hero"
    >
        {/* Soft warm radial ambient — matches the gold accent without changing the global theme */}
        <div
            className="absolute inset-0 pointer-events-none -z-10"
            aria-hidden="true"
            style={{
                background:
                    'radial-gradient(55% 50% at 50% 0%, rgba(181, 135, 26, 0.10) 0%, transparent 70%),' +
                    'radial-gradient(40% 35% at 90% 90%, rgba(181, 135, 26, 0.06) 0%, transparent 70%)',
            }}
        />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p
                className="font-mono text-[11px] sm:text-[12px] tracking-[0.22em] uppercase font-semibold"
                style={{ color: GOLD }}
                data-testid="for-good-eyebrow"
            >
                RevoloAIWEB for Good
            </p>
            <h1
                className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1A1A2E] leading-[1.08] text-balance"
                data-testid="for-good-title"
            >
                Digital tools that should never have been out of reach.
            </h1>
            <p className="mt-6 text-[17px] sm:text-[18px] text-[#4A4A5A] leading-relaxed max-w-2xl mx-auto">
                Discounted websites, AI tooling and digital training for UK non-profits,
                charities and community organisations.
            </p>
            <a
                href={MAILTO}
                className="mt-9 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold text-[15px] transition-opacity hover:opacity-95"
                style={{
                    backgroundColor: GOLD,
                    boxShadow: '0 6px 22px rgba(181, 135, 26, 0.30), 0 12px 40px rgba(181, 135, 26, 0.18)',
                }}
                data-testid="for-good-hero-cta"
            >
                <Mail className="w-4 h-4" />
                Tell us about your organisation
                <ArrowRight className="w-4 h-4" />
            </a>
        </div>
    </section>
);

/* ============================================================
   2. Trust strip
   ============================================================ */
const TrustStrip = () => (
    <section className="py-6 border-y border-[#EFEAE0]" data-testid="for-good-trust">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-[12.5px] sm:text-[13px] text-[#6B6B7A] leading-relaxed">
                A programme by Artemis Business Solutions Ltd · Company No. 17087203 ·
                ICO ZC156063 · Operating under UK GDPR &amp; DPA 2018
            </p>
        </div>
    </section>
);

/* ============================================================
   3. Why this exists
   ============================================================ */
const WhyExists = () => (
    <section className="py-24 sm:py-28" data-testid="for-good-why">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionLabel>Why this exists</SectionLabel>
            <p className="mt-6 font-display text-2xl sm:text-3xl md:text-[32px] font-semibold tracking-tight text-[#1A1A2E] leading-[1.35] text-balance">
                Many UK charities and community groups still operate on outdated websites,
                manual processes, or no digital presence at all. Commercial agency prices
                put modern tools out of reach. RevoloAIWEB for Good closes that gap.
            </p>
        </div>
    </section>
);

/* ============================================================
   4. Who it's for
   ============================================================ */
const WhoItsFor = () => {
    const cards = [
        {
            icon: Heart,
            title: 'Registered charities',
            body: 'Small to mid-sized UK charities (typically under £1m income), registered with the Charity Commission.',
        },
        {
            icon: Building2,
            title: 'Community Interest Companies',
            body: 'CICs with an asset lock and a social mission in their constitution.',
        },
        {
            icon: Users,
            title: 'Community groups & social enterprises',
            body: 'Unincorporated groups, mutual aid networks, CIOs and social enterprises delivering public benefit.',
        },
    ];
    return (
        <section className="py-24 bg-[#FBF8F0]" data-testid="for-good-who">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <SectionLabel>Who it&rsquo;s for</SectionLabel>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A2E]">
                        Built for the organisations doing the work.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="for-good-who-grid">
                    {cards.map(({ icon: Icon, title, body }, i) => (
                        <div
                            key={title}
                            className="p-7 rounded-2xl bg-white border border-[#EFEAE0] h-full"
                            data-testid={`for-good-who-card-${i + 1}`}
                            style={{ boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)' }}
                        >
                            <div
                                className="inline-flex items-center justify-center w-12 h-12 rounded-xl"
                                style={{ backgroundColor: GOLD_SOFT, color: GOLD }}
                            >
                                <Icon className="w-5 h-5" />
                            </div>
                            <h3 className="mt-5 font-display text-[19px] font-semibold tracking-tight text-[#1A1A2E]">
                                {title}
                            </h3>
                            <p className="mt-3 text-[#4A4A5A] text-[15px] leading-relaxed">
                                {body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ============================================================
   5. What you can get — detailed pricing
   ============================================================ */
const WhatYouCanGet = () => {
    const offerings = [
        {
            icon: Globe,
            title: 'Subsidised website',
            summary:
                'A modern, accessible, multilingual website built on the same secure stack we use for our own products.',
            included: [
                'Up to 6 pages (Home, About, Services/Programmes, News, Contact, custom)',
                'Mobile-first responsive design, accessibility-aware (WCAG 2.1 AA target)',
                'Privacy-first analytics (no Google Analytics, no third-party trackers)',
                'Bilingual or multilingual content (English + one other language included)',
                'SSL certificate, secure hosting setup, automated backups',
                'Simple admin you can update yourself (no coding required)',
                '30 days of post-launch support',
                'Delivery in 4–8 weeks',
            ],
            excluded: [
                'E-commerce / online donations',
                'Custom integrations (CRM, mailing list, booking systems)',
                'Original photography or copywriting',
                'Translation into additional languages beyond the first',
            ],
            priceMain: 'From £1,200',
            priceStrike: '£3,000+',
            priceNote: 'One-off · no recurring fees · optional care plan from £49/month',
            ctaLabel: 'Enquire about a website',
            ctaHref:
                'mailto:revoloai@protonmail.com?subject=For%20Good%20%E2%80%94%20website%20enquiry',
            ctaTestId: 'for-good-offer-cta-website',
        },
        {
            icon: Sparkles,
            title: 'AI tooling for non-profits',
            summary:
                'Practical AI helpers tailored to your work, not generic chatbots.',
            included: [
                'Discovery call to define exactly what the tool needs to do',
                'One custom AI tool built and tested (e.g. multilingual assistant, document summariser, eligibility checker)',
                'Integration with one of your existing systems where feasible',
                'Privacy-first build: no service-user data sold or shared',
                '30 days of support after handover',
                'Written handover document so your team can manage it',
            ],
            excluded: [
                'Ongoing API/usage costs (paid by you directly to provider, e.g. Anthropic, OpenAI)',
                'Hosting beyond the first 30 days (we can recommend low-cost options)',
                'Maintenance / updates beyond the first 30 days (optional care plan)',
            ],
            priceMain: 'From £600 (small scoped tool) · From £2,500 (mid-size project)',
            priceStrike: 'Commercial equivalents start at £1,800 and £6,000',
            priceNote:
                'Grant-funded options available — we can build into your grant application',
            ctaLabel: 'Discuss an AI tool',
            ctaHref:
                'mailto:revoloai@protonmail.com?subject=For%20Good%20%E2%80%94%20AI%20tooling%20enquiry',
            ctaTestId: 'for-good-offer-cta-ai',
        },
        {
            icon: Wrench,
            title: 'Digital literacy workshops',
            summary:
                '2–3 hour sessions for your team and volunteers — safe, ethical, practical use of AI.',
            included: [
                '2–3 hour session (online via your meeting tool, or in-person within London)',
                'Up to 15 participants',
                'Covers: safe AI use, prompt-writing for grant applications and reports, translation, summarisation, data protection basics',
                'Printable handouts, a reusable prompt library, and a recording (online sessions)',
                'Post-session Q&A email channel for 14 days',
            ],
            excluded: [
                'Travel costs outside London (quoted separately if needed)',
                'Custom training material beyond the standard curriculum (available as an add-on)',
                '1-to-1 coaching for individual staff (available separately)',
            ],
            priceMain: '£250 per session',
            priceStrike: '£600 commercial rate',
            priceNote:
                'Free for first cohort partners — first 3 organisations that book pay nothing · Bundle of 3 sessions: £600',
            ctaLabel: 'Book a workshop',
            ctaHref:
                'mailto:revoloai@protonmail.com?subject=For%20Good%20%E2%80%94%20workshop%20booking',
            ctaTestId: 'for-good-offer-cta-workshop',
        },
        {
            icon: Users,
            title: 'Sector AI-service sites',
            summary:
                'Public AI tools serving a whole community, free for end users, funded by a sponsor or grant.',
            included: [
                'Full discovery & scoping with you and the funder',
                'Custom public-facing tool (e.g. signposting site, eligibility checker, multilingual support locator)',
                'Multilingual interface (up to 6 languages from the Revoloai standard set)',
                'Privacy-first, no user accounts required by default',
                'Full handover: code, hosting setup, documentation',
                '90 days of post-launch support',
                'One impact report at the 90-day mark, for the funder',
            ],
            excluded: [
                'Ongoing hosting beyond 90 days (we recommend low-cost options or include in grant)',
                'Major redesigns after launch (covered by optional care plan)',
                'User research / interviews (available as a separate workstream)',
            ],
            priceMain: 'Custom — typically £5,000 to £15,000 depending on scope',
            priceStrike: null,
            priceNote:
                'Funded by grants or CSR partners — usually not paid directly by the partner non-profit · We can help you build the budget into a grant application',
            ctaLabel: 'Propose a sector project',
            ctaHref:
                'mailto:revoloai@protonmail.com?subject=For%20Good%20%E2%80%94%20sector%20project%20enquiry',
            ctaTestId: 'for-good-offer-cta-sector',
        },
    ];

    return (
        <section className="py-24" data-testid="for-good-offer">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <SectionLabel>What you can get</SectionLabel>
                    <h2
                        className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A2E]"
                        data-testid="for-good-offer-title"
                    >
                        Four offers. Clear prices. No surprises.
                    </h2>
                    <p className="mt-5 text-[15.5px] sm:text-[16px] text-[#4A4A5A] leading-relaxed">
                        All prices below are the discounted &ldquo;for Good&rdquo; rates for
                        eligible UK non-profits. Full commercial prices shown in grey for
                        reference. Final pricing confirmed in writing before any work starts.
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    data-testid="for-good-offer-grid"
                >
                    {offerings.map(
                        (
                            {
                                icon: Icon, title, summary, included, excluded,
                                priceMain, priceStrike, priceNote,
                                ctaLabel, ctaHref, ctaTestId,
                            },
                            i,
                        ) => (
                            <div
                                key={title}
                                className="p-7 sm:p-8 rounded-2xl bg-white border border-[#EFEAE0] h-full flex flex-col"
                                data-testid={`for-good-offer-card-${i + 1}`}
                                style={{ boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)' }}
                            >
                                {/* Header */}
                                <div className="flex items-start gap-4">
                                    <div
                                        className="inline-flex items-center justify-center w-12 h-12 rounded-xl shrink-0"
                                        style={{ backgroundColor: GOLD_SOFT, color: GOLD }}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-display text-[20px] sm:text-[21px] font-semibold tracking-tight text-[#1A1A2E] leading-tight">
                                            {title}
                                        </h3>
                                        <p className="mt-2 text-[#4A4A5A] text-[14.5px] leading-relaxed">
                                            {summary}
                                        </p>
                                    </div>
                                </div>

                                {/* Included */}
                                <div className="mt-6">
                                    <p
                                        className="font-mono text-[10.5px] tracking-[0.2em] uppercase font-semibold mb-3"
                                        style={{ color: GOLD }}
                                    >
                                        What&rsquo;s included
                                    </p>
                                    <ul className="space-y-2.5">
                                        {included.map((line) => (
                                            <li
                                                key={line}
                                                className="flex items-start gap-2.5 text-[14px] text-[#1A1A2E] leading-relaxed"
                                            >
                                                <span
                                                    className="inline-flex w-5 h-5 rounded-full items-center justify-center shrink-0 mt-0.5"
                                                    style={{ backgroundColor: GOLD_SOFT, color: GOLD }}
                                                >
                                                    <Check className="w-3 h-3" strokeWidth={3} />
                                                </span>
                                                <span>{line}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Excluded */}
                                <div className="mt-6">
                                    <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase font-semibold mb-3 text-[#8A8A95]">
                                        What&rsquo;s NOT included
                                    </p>
                                    <ul className="space-y-2.5">
                                        {excluded.map((line) => (
                                            <li
                                                key={line}
                                                className="flex items-start gap-2.5 text-[14px] text-[#6B6B7A] leading-relaxed"
                                            >
                                                <span
                                                    className="inline-flex w-5 h-5 rounded-full items-center justify-center shrink-0 mt-0.5 bg-[#F4F1E8] text-[#A8A8B2]"
                                                >
                                                    <X className="w-3 h-3" strokeWidth={3} />
                                                </span>
                                                <span>{line}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Price block — sticks to bottom */}
                                <div
                                    className="mt-7 pt-5 border-t border-[#EFEAE0]"
                                    data-testid={`for-good-offer-price-${i + 1}`}
                                >
                                    <p
                                        className="font-display text-[20px] sm:text-[22px] font-semibold tracking-tight leading-tight"
                                        style={{ color: GOLD }}
                                    >
                                        {priceMain}
                                    </p>
                                    {priceStrike && (
                                        <p className="mt-1 text-[13.5px] text-[#8A8A95]">
                                            <span className="line-through">{priceStrike}</span>{' '}
                                            <span className="italic">(standard commercial rate)</span>
                                        </p>
                                    )}
                                    <p className="mt-2 text-[13px] text-[#6B6B7A] leading-relaxed">
                                        {priceNote}
                                    </p>
                                </div>

                                {/* Mini CTA */}
                                <a
                                    href={ctaHref}
                                    className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white font-semibold text-[14px] self-start transition-opacity hover:opacity-95"
                                    style={{
                                        backgroundColor: GOLD,
                                        boxShadow:
                                            '0 4px 16px rgba(181, 135, 26, 0.25), 0 8px 28px rgba(181, 135, 26, 0.15)',
                                    }}
                                    data-testid={ctaTestId}
                                >
                                    <Mail className="w-4 h-4" />
                                    {ctaLabel}
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                            </div>
                        ),
                    )}
                </div>

                <p
                    className="mt-10 text-center text-[12.5px] sm:text-[13px] text-[#6B6B7A] leading-relaxed max-w-3xl mx-auto"
                    data-testid="for-good-offer-eligibility"
                >
                    Eligibility for &ldquo;for Good&rdquo; rates: UK-registered charities, CICs,
                    social enterprises, CIOs and unincorporated community groups delivering
                    public benefit. We confirm eligibility before quoting.
                </p>
            </div>
        </section>
    );
};

/* ============================================================
   6. How it works
   ============================================================ */
const HowItWorks = () => {
    const steps = [
        { title: 'You tell us about your work', body: 'A short email or call. We listen, no commitment.' },
        { title: 'We propose a fit', body: 'A clear written scope with the discount applied, in plain English.' },
        { title: 'We build together', body: 'You stay involved at every stage.' },
        { title: 'You own it', body: 'You keep the code, content and accounts. Optional ongoing support.' },
    ];
    return (
        <section className="py-24 bg-[#FBF8F0]" data-testid="for-good-how">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <SectionLabel>How it works</SectionLabel>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A2E]">
                        Four steps. No surprises.
                    </h2>
                </div>
                <ol
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                    data-testid="for-good-how-list"
                >
                    {steps.map((s, i) => (
                        <li
                            key={s.title}
                            className="p-7 rounded-2xl bg-white border border-[#EFEAE0] h-full"
                            data-testid={`for-good-how-step-${i + 1}`}
                            style={{ boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)' }}
                        >
                            <span
                                className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold text-[15px]"
                                style={{
                                    backgroundColor: GOLD,
                                    boxShadow: '0 4px 14px rgba(181, 135, 26, 0.30)',
                                }}
                                aria-hidden="true"
                            >
                                {i + 1}
                            </span>
                            <h3 className="mt-5 font-display text-[17px] font-semibold tracking-tight text-[#1A1A2E]">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-[#4A4A5A] text-[14.5px] leading-relaxed">
                                {s.body}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
};

/* ============================================================
   7. What we don't do
   ============================================================ */
const WhatWeDontDo = () => (
    <section className="py-24" data-testid="for-good-dontdo">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionLabel>What we don&rsquo;t do</SectionLabel>
            <p className="mt-6 text-[17px] sm:text-[18px] text-[#4A4A5A] leading-[1.75]">
                We don&rsquo;t do free work in exchange for &ldquo;exposure&rdquo;, we don&rsquo;t lock
                you into recurring contracts, and we don&rsquo;t share or sell your data or your
                service users&rsquo; data — ever. If we can&rsquo;t deliver what you need at a fair
                price, we&rsquo;ll tell you, and where possible we&rsquo;ll point you to someone who
                can.
            </p>
        </div>
    </section>
);

/* ============================================================
   8. FAQ
   ============================================================ */
const FAQ_ITEMS = [
    {
        q: 'How discounted is "discounted"?',
        a: "Up to 60% off our standard commercial rates, depending on your organisation's size and the project. The smallest charities pay the least. We're transparent about pricing in writing before any work starts.",
    },
    {
        q: 'Do you only work with registered charities?',
        a: 'No. Registered charities, CICs, social enterprises, CIOs and unincorporated community groups — as long as the work delivers public benefit.',
    },
    {
        q: 'What about grant funding?',
        a: 'We can build projects funded by grants you already hold, and in some cases help you identify or apply for funding (National Lottery Community Fund, Catalyst, Tech for Good).',
    },
    {
        q: 'Who owns the website or tool you build?',
        a: 'You do. Code, domain, accounts, content — all yours.',
    },
    {
        q: 'Where is our data stored?',
        a: 'UK-compliant, privacy-first infrastructure. ICO-registered (ZC156063), under UK GDPR. No ad trackers, no profiling analytics, no third-party scripts unless you ask.',
    },
    {
        q: 'How do I get started?',
        a: 'Email revoloai@protonmail.com with a sentence or two about your organisation. Reply within 3 working days.',
    },
];

const FAQSection = () => {
    const [open, setOpen] = useState(null);
    return (
        <section className="py-24 bg-[#FBF8F0]" data-testid="for-good-faq">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <SectionLabel>FAQ</SectionLabel>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A2E]">
                        Honest answers.
                    </h2>
                </div>
                <div
                    className="rounded-2xl bg-white border border-[#EFEAE0] divide-y divide-[#EFEAE0]"
                    data-testid="for-good-faq-list"
                    style={{ boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)' }}
                >
                    {FAQ_ITEMS.map((item, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={item.q} data-testid={`for-good-faq-item-${i + 1}`}>
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? null : i)}
                                    aria-expanded={isOpen}
                                    aria-controls={`for-good-faq-answer-${i + 1}`}
                                    className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left hover:bg-[#FBF8F0] transition-colors"
                                    data-testid={`for-good-faq-toggle-${i + 1}`}
                                >
                                    <span className="font-display text-[16.5px] sm:text-[17px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {item.q}
                                    </span>
                                    <span
                                        className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 mt-0.5"
                                        style={{
                                            backgroundColor: isOpen ? GOLD : GOLD_SOFT,
                                            color: isOpen ? '#FFFFFF' : GOLD,
                                            border: `1px solid ${GOLD_BORDER}`,
                                        }}
                                        aria-hidden="true"
                                    >
                                        {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div
                                        id={`for-good-faq-answer-${i + 1}`}
                                        className="px-6 pb-5 text-[15px] text-[#4A4A5A] leading-relaxed"
                                        data-testid={`for-good-faq-answer-${i + 1}`}
                                    >
                                        {item.a}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ============================================================
   9. Closing CTA
   ============================================================ */
const ClosingCTA = () => (
    <section className="py-24 sm:py-28" data-testid="for-good-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1A1A2E] text-balance">
                Let&rsquo;s see if we can help.
            </h2>
            <p className="mt-5 text-[17px] sm:text-[18px] text-[#4A4A5A] leading-relaxed max-w-xl mx-auto">
                A short email is enough to start. No forms, no commitment, no sales pitch.
            </p>
            <a
                href={MAILTO}
                className="mt-9 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-[16px] transition-opacity hover:opacity-95"
                style={{
                    backgroundColor: GOLD,
                    boxShadow: '0 8px 28px rgba(181, 135, 26, 0.35), 0 16px 48px rgba(181, 135, 26, 0.20)',
                }}
                data-testid="for-good-closing-cta"
            >
                <Mail className="w-5 h-5" />
                Email us
            </a>
            <p className="mt-7 text-[12.5px] sm:text-[13px] text-[#6B6B7A] leading-relaxed">
                revoloai@protonmail.com · Replies within 3 working days · Artemis Business
                Solutions Ltd · 124–128 City Road, London EC1V 2NX
            </p>
        </div>
    </section>
);

/* ============================================================
   Local SectionLabel — small gold eyebrow, scoped to this page only
   ============================================================ */
const SectionLabel = ({ children }) => (
    <p
        className="font-mono text-[10.5px] tracking-[0.22em] uppercase font-semibold"
        style={{ color: GOLD }}
    >
        {children}
    </p>
);

export default ForGood;
